import { useState } from "react";
import { AlertTriangle, CalendarCheck, Check, Loader2, Receipt } from "lucide-react";
import { toast } from "sonner";

import { marcarPaga, mensagemDeErro } from "../api";
import { dataCurta, diaSemana, reais } from "../lib/formato";

function ContaLinha({ c, aoPagar }) {
  const [pagando, setPagando] = useState(false);
  const pagar = async () => {
    setPagando(true);
    try { await marcarPaga(c.id); toast.success(`"${c.nome}" marcada como paga`); aoPagar(); } catch (e) {
      toast.error(mensagemDeErro(e, "Não consegui marcar como paga."));
    } finally { setPagando(false); }
  };
  return (
    <li className={`flex items-center gap-3 px-4 py-2.5 ${c.vencida ? "bg-red-500/5" : ""}`} data-e2e="conta-a-pagar">
      <div className={`w-12 text-center shrink-0 ${c.vencida ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}>
        <p className="text-sm font-bold tabular-nums leading-none">{dataCurta(c.vencimento)}</p>
        <p className="text-[10px] uppercase mt-0.5">{c.vencida ? "vencida" : diaSemana(c.vencimento)}</p>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{c.nome}</p>
        <p className="text-[11px] text-muted-foreground truncate">{c.classificacao}{c.fornecedor ? ` · ${c.fornecedor}` : ""}</p>
      </div>
      <span className="tabular-nums font-semibold text-sm">{reais(c.valor)}</span>
      <button type="button" onClick={pagar} disabled={pagando} title="Marcar como paga hoje"
        className="h-8 px-2.5 rounded-lg border text-xs font-medium hover:bg-emerald-500/10 hover:border-emerald-500/40 flex items-center gap-1 disabled:opacity-50">
        {pagando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}Paga
      </button>
    </li>
  );
}

function Cabecalho({ icone: Icone, titulo, dica }) {
  return (
    <div className="px-4 py-3 border-b flex items-center gap-2">
      <Icone className="w-4 h-4 text-primary" /><h2 className="font-bold">{titulo}</h2>
      <span className="text-xs text-muted-foreground ml-auto">{dica}</span>
    </div>
  );
}

/** Contas a pagar (vencidas no topo, com baixa num toque) e o que vai cair na conta. */
export default function ContasERecebimentos({ dados, aoMudar }) {
  const contas = dados?.contas_a_pagar || [];
  const receb = dados?.proximos_recebimentos || [];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-2xl border bg-card overflow-hidden" data-e2e="contas-a-pagar">
        <Cabecalho icone={Receipt} titulo="Contas a pagar" dica="próximos 30 dias e vencidas" />
        {!contas.length ? <p className="p-5 text-sm text-muted-foreground">Nenhuma conta em aberto. 🎉</p> : (
          <ul className="divide-y max-h-[420px] overflow-y-auto">{contas.map((c) => <ContaLinha key={c.id} c={c} aoPagar={aoMudar} />)}</ul>
        )}
        {contas.some((c) => c.vencida) && (
          <p className="px-4 py-2 border-t text-[11px] text-red-600 dark:text-red-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />Vencidas entram na previsão como se fossem pagas hoje.</p>
        )}
      </section>
      <section className="rounded-2xl border bg-card overflow-hidden" data-e2e="proximos-recebimentos">
        <Cabecalho icone={CalendarCheck} titulo="Vai cair na conta" dica="próximos 30 dias, já sem taxa" />
        {!receb.length ? <p className="p-5 text-sm text-muted-foreground">Nada a receber — as vendas já entraram ou as formas estão configuradas para receber na hora.</p> : (
          <ul className="divide-y max-h-[420px] overflow-y-auto">
            {receb.map((r) => (
              <li key={`${r.data}-${r.forma}`} className="flex items-center gap-3 px-4 py-2.5">
                <div className="w-12 text-center shrink-0 text-muted-foreground">
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">{dataCurta(r.data)}</p>
                  <p className="text-[10px] uppercase mt-0.5">{diaSemana(r.data)}</p>
                </div>
                <span className="flex-1 text-sm truncate">{r.forma}</span>
                <span className="tabular-nums font-semibold text-sm text-emerald-700 dark:text-emerald-400">{reais(r.valor)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
